"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const rows = [{
  number: "01",
  eyebrow: "REVESTIMENTOS & COBERTURAS",
  headline: "Revestimento em Produtos de Padaria",
  body: "Um revestimento ligeiro de óleo é frequentemente aplicado antes do cozimento, e conseguir consistentemente a quantidade certa é um desafio. A clara de ovo é outra aplicação comum de revestimento, aplicada em pães e pastéis antes do cozimento.",
  certs: ["ISO 9001", "CE"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-15.18.08.png?v=0909pspczjxg",
  alt: "Sistema de pulverização industrial de alta precisão em ambiente fabril",
  imageLeft: true
}, {
  number: "02",
  eyebrow: "REVESTIMENTOS & COBERTURAS",
  headline: "Revestimento de Tabuleiros",
  body: "Os agentes de desmoldagem são mais eficazes e económicos quando aplicados como um revestimento fino e uniforme. Limitar a sua aplicação às zonas onde o produto entra em contacto com o tabuleiro ou a correia transportadora reduz a quantidade utilizada e simplifica as atividades de limpeza.",
  certs: ["Patente 1993", "Cert. Qualidade"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-15.18.14.png?v=z1y8wth9jog",
  alt: "Equipamento de pulverização automatizado em linha de montagem",
  imageLeft: false
}, {
  number: "03",
  eyebrow: "REVESTIMENTOS & COBERTURAS",
  headline: "Revestimento com Materiais Viscosos",
  body: "Os Sistemas de Pulverização Aquecidos AccuCoat® são ideais para a pulverização de manteiga, chocolate, barreiras de gordura, óleo, suspensão de açúcar, glacê e outros materiais de revestimento viscosos. Estes sistemas proporcionam um controlo preciso da temperatura para eliminar o desperdício causado por revestimentos demasiado quentes ou demasiado frios.",
  certs: ["Suporte 24/7", "Expertise"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-15.18.25.png?v=il3r7l4cixb",
  alt: "Engenheiro a realizar manutenção técnica num sistema de pulverização",
  imageLeft: true
}];
export default function Bakeryshowcase() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".intro-eyebrow", {
        y: 16
      });
      gsap.set(".intro-heading", {
        y: 24
      });
      gsap.to(".intro-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".intro-block",
          start: "top 85%",
          once: true
        }
      });
      gsap.to(".intro-heading", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.1,
        scrollTrigger: {
          trigger: ".intro-block",
          start: "top 85%",
          once: true
        }
      });
      const rowEls = gsap.utils.toArray<HTMLElement>(".feature-row");
      rowEls.forEach(row => {
        const imageWrap = row.querySelector(".row-image-wrap");
        const textEls = row.querySelectorAll(".row-text > *");
        if (imageWrap) {
          gsap.set(imageWrap, {
            y: 40
          });
          gsap.to(imageWrap, {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: row,
              start: "top 80%",
              once: true
            }
          });
          const img = imageWrap.querySelector(".row-image");
          if (img) {
            gsap.to(img, {
              y: -20,
              ease: "none",
              scrollTrigger: {
                trigger: row,
                start: "top bottom",
                end: "bottom top",
                scrub: true
              }
            });
          }
        }
        if (textEls.length) {
          gsap.set(textEls, {
            y: 24
          });
          gsap.to(textEls, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            stagger: 0.08,
            scrollTrigger: {
              trigger: row,
              start: "top 80%",
              once: true
            }
          });
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".intro-eyebrow", ".intro-heading", ".row-image-wrap", ".row-text > *"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31452" id="bakeryshowcase" className="bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-12">
        <div className="intro-block mx-auto max-w-[720px] text-center">
          <div className="intro-eyebrow inline-flex items-center gap-2 rounded-lg border px-4 py-2 border-border" style={{
          opacity: 0
        }}>
            <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Excelência Eurocontrol
            </span>
          </div>
          <h2 className="intro-heading font-default mt-8 text-4xl md:text-6xl lg:text-7xl font-normal text-foreground" style={{
          opacity: 0,
          letterSpacing: "-0.03em",
          lineHeight: 1.02
        }}>Pastelaria &amp; Panificadoras</h2>
          <p className="mt-6 text-base md:text-lg font-light text-muted-foreground" style={{
          lineHeight: 1.55
        }}>Pode ser um desafio encontrar os métodos mais eficazes para a aplicação de clara de ovo, lubrificação de tabuleiros, aplicação de revestimentos viscosos e limpeza de equipamentos de processamento. A tecnologia de pulverização da Spraying Systems Co. pode ajudar — e ter um impacto significativo nos resultados financeiros. Os nossos especialistas podem automatizar as operações de pulverização para garantir um revestimento uniforme, eliminar o desperdício de ingredientes dispendiosos e otimizar a aplicação de inibidores de bolores para prolongar a vida útil. Com mais de oito décadas de experiência na indústria de padaria, podemos ajudá-lo a atingir os seus objetivos de desempenho e sustentabilidade.</p>
        </div>

        <div className="mt-24 md:mt-32 flex flex-col gap-24 md:gap-32">
          {rows.map((row, i) => <div key={row.number} data-index={i} className="feature-row grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              <div className={`row-image-wrap ${row.imageLeft ? "lg:col-span-7 lg:order-1" : "lg:col-span-7 lg:order-2"} order-1 relative`} style={{
            opacity: 0
          }}>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-0 -z-10 bg-accent/20 blur-3xl" aria-hidden="true" />
                  <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
                    <img src={row.image} alt={row.alt} loading="lazy" decoding="async" className="row-image w-full h-[360px] md:h-[460px] lg:h-[520px] object-cover" />
                  </div>
                </div>
              </div>

              <div className={`row-text ${row.imageLeft ? "lg:col-span-5 lg:order-2" : "lg:col-span-5 lg:order-1"} order-2`}>
                <div className="font-serif text-4xl md:text-5xl font-normal text-primary" style={{
              opacity: 0,
              letterSpacing: "-0.02em",
              lineHeight: 1
            }}>
                  {row.number}
                </div>
                <div className="mt-6 text-xs font-medium uppercase tracking-wider text-muted-foreground" style={{
              opacity: 0
            }}>
                  {row.eyebrow}
                </div>
                <h3 className="font-default mt-4 text-2xl md:text-3xl lg:text-4xl font-medium text-foreground" style={{
              opacity: 0,
              letterSpacing: "-0.02em",
              lineHeight: 1.12
            }}>
                  {row.headline}
                </h3>
                <p className="mt-5 text-base md:text-lg font-light text-muted-foreground" style={{
              opacity: 0,
              lineHeight: 1.55
            }}>
                  {row.body}
                </p>
                <div className="mt-6 flex flex-wrap gap-2" style={{
              opacity: 0
            }}>
                  {row.certs.map((cert, j) => <Badge key={cert} data-index={j} variant="outline" className="rounded-lg px-3 py-1 text-[11px] font-medium uppercase tracking-wider border-border bg-muted/50 text-foreground">
                      {cert}
                    </Badge>)}
                </div>
              </div>
            </div>)}
        </div>

        <div className="mt-24 md:mt-32 flex justify-center">
          <Button asChild variant="ghost" className="group h-auto rounded-lg px-0 py-2 text-base font-normal hover:bg-transparent text-foreground">
            <Link to="/ContactsPage" className="inline-flex items-center gap-2">
              <span className="relative">
                Consulte-nos hoje
                <span className="absolute left-0 -bottom-1 h-px w-0 transition-all duration-300 group-hover:w-full bg-primary" />
              </span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 text-primary" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}